import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../App';
import { Activity, AlertTriangle, Clock, Users, LogIn, UserPlus, Server, Zap, CheckCircle2, XCircle, RefreshCcw, UploadCloud } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

function MetricCard({ icon: Icon, label, value, sub, color = 'text-[#D4AF37]' }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-xs text-stone-500">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-[#1A1A1A]" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</p>
      {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}

const HEALTH_STYLES = {
  ok:             { pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', label: 'OK' },
  degraded:       { pill: 'bg-amber-50 text-amber-700 border-amber-200',       dot: 'bg-amber-500',   label: 'Degraded' },
  down:           { pill: 'bg-red-50 text-red-700 border-red-200',             dot: 'bg-red-500',     label: 'Down' },
  not_configured: { pill: 'bg-stone-50 text-stone-600 border-stone-200',       dot: 'bg-stone-400',   label: 'Not configured' },
};

const REASON_LABELS = {
  mime: { label: 'MIME', tone: 'bg-amber-50 text-amber-700 border-amber-200', hint: 'Unsupported Content-Type' },
  sniff: { label: 'Sniff', tone: 'bg-red-50 text-red-700 border-red-200', hint: 'Magic-bytes did not match a video container' },
  size: { label: 'Size', tone: 'bg-blue-50 text-blue-700 border-blue-200', hint: 'Over the per-type size cap' },
  transcode: { label: 'Transcode', tone: 'bg-purple-50 text-purple-700 border-purple-200', hint: 'ffmpeg failed on the source' },
  count_limit: { label: 'Limit', tone: 'bg-stone-100 text-stone-700 border-stone-200', hint: 'Provider hit the per-type item cap' },
};

const KIND_LABELS = {
  video: 'Videos',
  portfolio_image: 'Portfolio',
  profile_image: 'Profile',
};
const KIND_ORDER = ['video', 'portfolio_image', 'profile_image'];

function RejectedUploadsCard({ data, loading, kind, onKindChange, onRefresh }) {
  const total = data?.total || 0;
  const byReason = data?.by_reason || {};
  const byKind = data?.by_kind || {};
  const recent = data?.recent || [];
  const sinceHours = data?.since_hours || 24;
  const allKindsTotal = useMemo(
    () => Object.values(byKind).reduce((a, b) => a + b, 0),
    [byKind]
  );

  return (
    <div className="card-surface p-4 sm:p-6" data-testid="admin-rejected-uploads-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <UploadCloud className="h-5 w-5 text-[#D4AF37]" />
            Rejected uploads, last {sinceHours}h
          </h3>
          <p className="text-xs text-stone-400 mt-0.5">
            Uploads blocked before storage, per type. Spikes in &ldquo;Sniff&rdquo; can signal a probe, spikes in &ldquo;Size&rdquo; mean the cap may need raising.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="text-xs text-stone-500 hover:text-[#D4AF37] flex items-center gap-1 disabled:opacity-50"
          data-testid="admin-rejected-uploads-refresh"
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Kind filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="rejected-kind-filter">
        <button
          onClick={() => onKindChange(null)}
          className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
            !kind ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-[#D4AF37]'
          }`}
          data-testid="rejected-kind-all"
        >
          All ({allKindsTotal})
        </button>
        {KIND_ORDER.map((k) => (
          <button
            key={k}
            onClick={() => onKindChange(k)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              kind === k ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-[#D4AF37]'
            }`}
            data-testid={`rejected-kind-${k}`}
          >
            {KIND_LABELS[k]} ({byKind[k] || 0})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span
          className="px-3 py-1 rounded-full text-sm font-medium bg-stone-100 text-stone-700 border border-stone-200"
          data-testid="rejected-uploads-total"
        >
          {total} total
        </span>
        {Object.keys(REASON_LABELS).map((key) => {
          const count = byReason[key] || 0;
          const style = REASON_LABELS[key];
          return (
            <span
              key={key}
              title={style.hint}
              className={`px-3 py-1 rounded-full text-xs font-medium border ${style.tone} ${count === 0 ? 'opacity-40' : ''}`}
              data-testid={`rejected-reason-${key}`}
            >
              {style.label}: {count}
            </span>
          );
        })}
      </div>

      {recent.length === 0 ? (
        <p className="text-sm text-stone-400" data-testid="rejected-uploads-empty">
          No rejected uploads in the window.
        </p>
      ) : (
        <div className="rounded-lg border border-[#E6E4DD] overflow-x-auto">
          <table className="w-full text-xs" data-testid="rejected-uploads-table">
            <thead className="bg-stone-50 text-stone-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">When</th>
                <th className="text-left px-3 py-2 font-medium">Kind</th>
                <th className="text-left px-3 py-2 font-medium">Reason</th>
                <th className="text-left px-3 py-2 font-medium">Provider</th>
                <th className="text-left px-3 py-2 font-medium">File</th>
                <th className="text-right px-3 py-2 font-medium">Size</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((row, idx) => {
                const ts = row.created_at ? new Date(row.created_at) : null;
                const when = ts
                  ? ts.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : '.';
                const reason = REASON_LABELS[row.reason] || { label: row.reason || 'unknown', tone: 'bg-stone-100 text-stone-700 border-stone-200' };
                const kindLabel = KIND_LABELS[row.kind] || row.kind || '.';
                const sizeKb = row.size_bytes != null ? `${Math.round(row.size_bytes / 1024)} KB` : '.';
                return (
                  <tr key={`rej-${row.created_at}-${idx}`} className="border-t border-[#E6E4DD]" data-testid={`rejected-row-${idx}`}>
                    <td className="px-3 py-2 text-stone-600 whitespace-nowrap">{when}</td>
                    <td className="px-3 py-2 text-stone-600">{kindLabel}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${reason.tone}`}>
                        {reason.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-stone-600 font-mono text-[11px]">{row.provider_id || '.'}</td>
                    <td className="px-3 py-2 text-stone-600 truncate max-w-[200px]">{row.filename || '.'}</td>
                    <td className="px-3 py-2 text-right text-stone-500">{sizeKb}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function HealthCard({ health, loading, onRefresh }) {
  const overall = HEALTH_STYLES[health?.status] || HEALTH_STYLES.not_configured;
  return (
    <div className="card-surface p-6" data-testid="admin-health-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Platform health</h3>
          {health && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${overall.pill}`}
                  data-testid="health-overall-status">
              {overall.label}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="text-xs text-stone-500 hover:text-[#D4AF37] inline-flex items-center gap-1 transition-colors disabled:opacity-50"
          data-testid="admin-health-refresh"
        >
          <RefreshCcw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(health?.services || []).map((s) => {
          const st = HEALTH_STYLES[s.status] || HEALTH_STYLES.not_configured;
          return (
            <div key={s.name} className="flex items-start gap-3 p-3 rounded-lg border border-[#E6E4DD] bg-white" data-testid={`health-service-${s.name}`}>
              <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${st.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-[#1A1A1A] text-sm">{s.name}</p>
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${st.pill}`}>
                    {st.label}
                  </span>
                  {typeof s.latency_ms === 'number' && (
                    <span className="text-[10px] text-stone-400">{s.latency_ms}ms</span>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed break-words">{s.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
      {health?.checked_at && (
        <p className="text-[11px] text-stone-400 mt-3">
          Checked {new Date(health.checked_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })} · cached {health.cache_ttl_seconds}s
        </p>
      )}
    </div>
  );
}

export function AdminMonitoringTab() {
  const [overview, setOverview] = useState(null);
  const [slowEndpoints, setSlowEndpoints] = useState([]);
  const [errors, setErrors] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [activity, setActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [rejected, setRejected] = useState(null);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedKind, setRejectedKind] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedError, setExpandedError] = useState(null);

  const fetchHealth = useCallback(async (force = false) => {
    setHealthLoading(true);
    try {
      const res = await api.get(`/admin/health${force ? '?force=true' : ''}`);
      setHealth(res.data);
    } catch {
      // Silent fail, just show nothing. This card is a nice-to-have.
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const fetchRejected = useCallback(async (kindFilter) => {
    setRejectedLoading(true);
    try {
      const params = new URLSearchParams({ since_hours: '24', limit: '20' });
      if (kindFilter) params.set('kind', kindFilter);
      const res = await api.get(`/admin/upload-rejections?${params.toString()}`);
      setRejected(res.data);
    } catch {
      // Silent fail, not critical.
    } finally {
      setRejectedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchHealth();
    fetchRejected(rejectedKind);
  }, [fetchHealth, fetchRejected, rejectedKind]);

  const fetchAll = async () => {
    try {
      const [overviewRes, slowRes, errorsRes, trafficRes, activityRes] = await Promise.all([
        api.get('/admin/monitoring/overview'),
        api.get('/admin/monitoring/slow-endpoints'),
        api.get('/admin/monitoring/errors?limit=10'),
        api.get('/admin/monitoring/traffic'),
        api.get('/admin/monitoring/activity'),
      ]);
      setOverview(overviewRes.data);
      setSlowEndpoints(slowRes.data || []);
      setErrors(errorsRes.data || []);
      setTraffic(trafficRes.data || []);
      setActivity(activityRes.data || []);
    } catch (err) {
      console.error('Monitoring data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monitoring-tab">
      {/* Metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={Activity} label="Requests (24h)" value={overview?.total_requests_24h || 0} />
        <MetricCard icon={AlertTriangle} label="Errors (24h)" value={overview?.server_errors_24h || 0} sub={`${overview?.error_rate_pct || 0}% error rate`} color="text-red-500" />
        <MetricCard icon={Users} label="Active Sessions" value={overview?.active_sessions || 0} color="text-green-600" />
        <MetricCard icon={Zap} label="Req/hour" value={overview?.requests_last_hour || 0} color="text-blue-500" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard icon={LogIn} label="Logins (24h)" value={overview?.logins_24h || 0} color="text-purple-500" />
        <MetricCard icon={UserPlus} label="Signups (24h)" value={overview?.signups_24h || 0} color="text-emerald-500" />
        <MetricCard icon={AlertTriangle} label="4xx Errors (24h)" value={overview?.client_errors_24h || 0} color="text-amber-500" />
        <MetricCard icon={Server} label="Server Status" value={health?.status ? (HEALTH_STYLES[health.status]?.label || ',') : 'Loading'} color={health?.status === 'ok' ? 'text-green-600' : health?.status === 'degraded' ? 'text-amber-500' : 'text-red-500'} />
      </div>

      <HealthCard health={health} loading={healthLoading} onRefresh={() => fetchHealth(true)} />

      <RejectedUploadsCard
        data={rejected}
        loading={rejectedLoading}
        kind={rejectedKind}
        onKindChange={setRejectedKind}
        onRefresh={() => fetchRejected(rejectedKind)}
      />

      {/* Traffic chart */}
      {traffic.length > 0 && (
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">Traffic (Last 24h)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={traffic}>
              <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E6E4DD', fontSize: 13 }} />
              <Bar dataKey="requests" fill="#D4AF37" radius={[3, 3, 0, 0]} name="Requests" />
              <Bar dataKey="errors" fill="#ef4444" radius={[3, 3, 0, 0]} name="Errors" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Auth activity chart */}
      {activity.length > 0 && (
        <div className="card-surface p-6">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">Auth Activity (Last 7 Days)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={activity}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E6E4DD', fontSize: 13 }} />
              <Legend />
              <Line type="monotone" dataKey="logins" stroke="#D4AF37" strokeWidth={2} name="Logins" dot={{ r: 3 }} />
              <Line type="monotone" dataKey="signups" stroke="#22c55e" strokeWidth={2} name="Signups" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Slow endpoints table */}
      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">
          <Clock className="h-4 w-4 inline mr-2 text-[#D4AF37]" />Slowest Endpoints (24h)
        </h3>
        {slowEndpoints.length === 0 ? (
          <p className="text-sm text-stone-400 text-center py-6">No request data yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E6E4DD]">
                  <th className="text-left py-2 px-3 text-stone-500 font-medium">Endpoint</th>
                  <th className="text-right py-2 px-3 text-stone-500 font-medium">Avg (ms)</th>
                  <th className="text-right py-2 px-3 text-stone-500 font-medium">Max (ms)</th>
                  <th className="text-right py-2 px-3 text-stone-500 font-medium">Hits</th>
                  <th className="text-right py-2 px-3 text-stone-500 font-medium">Errors</th>
                </tr>
              </thead>
              <tbody>
                {slowEndpoints.map((ep) => (
                  <tr key={`${ep.method}-${ep.path}`} className="border-b border-[#E6E4DD] hover:bg-[#F9F6F0]">
                    <td className="py-2 px-3 font-mono text-xs">
                      <span className={`inline-block w-12 text-center rounded text-[10px] font-bold mr-2 py-0.5 ${ep.method === 'GET' ? 'bg-blue-50 text-blue-600' : ep.method === 'POST' ? 'bg-green-50 text-green-600' : ep.method === 'PUT' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                        {ep.method}
                      </span>
                      {ep.path}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${ep.avg_ms > 1000 ? 'text-red-500 font-bold' : ep.avg_ms > 500 ? 'text-amber-500' : 'text-stone-600'}`}>
                      {ep.avg_ms}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-stone-500">{ep.max_ms}</td>
                    <td className="py-2 px-3 text-right text-stone-600">{ep.count}</td>
                    <td className={`py-2 px-3 text-right ${ep.error_count > 0 ? 'text-red-500 font-bold' : 'text-stone-400'}`}>{ep.error_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent errors */}
      <div className="card-surface p-6">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">
          <AlertTriangle className="h-4 w-4 inline mr-2 text-red-500" />Recent Errors
        </h3>
        {errors.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
              <Server className="h-6 w-6 text-green-500" />
            </div>
            <p className="text-sm text-stone-500">No server errors recorded</p>
          </div>
        ) : (
          <div className="space-y-2">
            {errors.map((err, idx) => {
              const errKey = `${err.timestamp}-${err.method}-${err.path}`;
              return (
              <div key={errKey} className="border border-red-100 rounded-lg overflow-hidden">
                <button
                  className="w-full text-left p-3 hover:bg-red-50 flex items-center justify-between gap-3"
                  onClick={() => setExpandedError(expandedError === errKey ? null : errKey)}
                  data-testid={`error-row-${idx}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded">{err.status}</span>
                    <span className="font-mono text-xs text-stone-700 truncate">{err.method} {err.path}</span>
                  </div>
                  <span className="text-xs text-stone-400 flex-shrink-0">
                    {err.timestamp ? new Date(err.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </button>
                {expandedError === errKey && err.error && (
                  <pre className="px-3 pb-3 text-xs text-red-700 bg-red-50 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {err.error}
                  </pre>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
